export class FilterParams {
  private params: URLSearchParams
  private baseUrl: string
  private originOverride: string
  private static readonly browserContextParams = new Set([
    'search_term',
    'tree_id',
    'brand_id',
    'tag_id',
  ])

  constructor(baseUrl: string = '', originOverride: string = '') {
    this.baseUrl = baseUrl
    this.originOverride = originOverride
    this.params = this.extractParams(baseUrl)
  }

  private getOrigin(): string {
    return this.originOverride || window.location.origin
  }

  private extractParams(url: string): URLSearchParams {
    if (!url) return new URLSearchParams()

    try {
      const urlObj = new URL(url, this.getOrigin())
      return new URLSearchParams(urlObj.search)
    } catch {
      return new URLSearchParams()
    }
  }

  getBasePathname(): string {
    if (!this.baseUrl) return ''

    try {
      const urlObj = new URL(this.baseUrl, this.getOrigin())
      return urlObj.pathname
    } catch {
      return ''
    }
  }

  setCategory(id: number): void {
    this.params.set('tree_id', String(id))
  }

  setFavorites(checked: boolean): void {
    this.params.set('is_favourite', String(checked))
  }

  setStock(inStock: boolean): void {
    this.params.set('in_stock', String(inStock))
  }

  toggleBrand(id: number, selected: boolean): void {
    this.toggleMultiValue('v', `1-${id}`, selected)
  }

  toggleProperty(propertyId: number, valueId: number, selected: boolean): void {
    this.toggleMultiValue('v', `2-${propertyId}-${valueId}`, selected)
  }

  setRange(propertyId: number, min: number, max: number): void {
    this.clearRange(propertyId)
    this.params.append('v', `8-${propertyId}-${min}-${max}`)
  }

  clearRange(propertyId: number): void {
    const prefix = `8-${propertyId}-`
    const remaining = this.params.getAll('v').filter(v => !v.startsWith(prefix))
    this.params.delete('v')
    remaining.forEach(v => this.params.append('v', v))
  }

  /**
   * Apply PLP routing context from element attributes (mirrors csc-plp-grid).
   * Non-empty strings win so theme can scope filters to category / brand / tag / search.
   */
  applyContextFromProps(
    context: Partial<Record<'tree_id' | 'brand_id' | 'tag_id' | 'search_term', string | undefined>>,
  ): void {
    for (const key of ['tree_id', 'brand_id', 'tag_id', 'search_term'] as const) {
      const value = context[key]
      if (value) this.params.set(key, value)
    }
  }

  private toggleMultiValue(key: string, value: string, add: boolean): void {
    if (add) {
      this.params.append(key, value)
    } else {
      const remaining = this.params.getAll(key).filter(v => v !== value)
      this.params.delete(key)
      remaining.forEach(v => this.params.append(key, v))
    }
  }

  buildUrl(): URL {
    const pathname = this.getBasePathname() || '/'
    const url = new URL(pathname, this.getOrigin())
    url.search = this.params.toString()
    return url
  }

  toString(): string {
    return this.params.toString()
  }

  getRefinementParamsString(): string {
    const refinementParams = new URLSearchParams()
    this.params.forEach((value, key) => {
      if (key === 'v') {
        refinementParams.append(key, value)
      }
    })
    return refinementParams.toString()
  }

  applyRefinementsFromUrl(url: string): void {
    try {
      const urlObj = new URL(url, this.getOrigin())
      const incomingParams = new URLSearchParams(urlObj.search)

      const preserved = new URLSearchParams()
      this.params.forEach((value, key) => {
        if (key !== 'v') {
          preserved.set(key, value)
        }
      })
      incomingParams.forEach((value, key) => {
        if (key === 'v') {
          preserved.append(key, value)
        } else if (FilterParams.browserContextParams.has(key)) {
          preserved.set(key, value)
        }
      })
      this.params = preserved
    } catch {
      return
    }
  }

  applyFromUrl(url: string): void {
    try {
      const urlObj = new URL(url, this.getOrigin())
      const incomingParams = new URLSearchParams(urlObj.search)

      const preserved = new URLSearchParams()
      this.params.forEach((value, key) => {
        if (key !== 'v') {
          preserved.set(key, value)
        }
      })

      incomingParams.forEach((value, key) => {
        if (key === 'v') {
          preserved.append(key, value)
        } else {
          preserved.set(key, value)
        }
      })

      this.params = preserved
    } catch {
      return
    }
  }

  clearSelectedFilters(): void {
    const preserved = new URLSearchParams()

    this.params.forEach((value, key) => {
      if (key === 'v') return
      preserved.append(key, value)
    })

    this.params = preserved
  }
}
